import React from 'react';
import { useMaintenanceMode } from './hooks/useMaintenanceMode';
import Maintenance from './components/pages/Maintenance';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/layout/Layout';
import Home from './components/pages/Home';
import Contact from './components/pages/Contact';
import Consent from './components/pages/Consent';
import Experience from './components/pages/Experience';
import Feedback from './components/pages/Feedback';
import Apply from './components/pages/Apply';
import EventApplication from './components/pages/EventApplication';
import EventsPage from './components/pages/EventsPage';
import EventDetailPage from './components/pages/EventDetailPage';
import Admin from './components/pages/Admin';
import QrScanner from './components/pages/QrScanner';
import SignIn from './components/auth/SignIn';
import SignUp from './components/auth/SignUp';
import PasswordReset from './components/auth/PasswordReset';
import UserProfile from './components/auth/UserProfile';
import ChooseUsername from './components/auth/ChooseUsername';
import EmailVerificationRedirect from './components/auth/EmailVerificationRedirect';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicRoute from './components/auth/PublicRoute';
import NotFound from './components/pages/NotFound';
import AdminRoleManager from './components/auth/AdminRoleManager';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { CookieConsentProvider } from './context/CookieConsentContext';
import ConditionalScriptLoader from './components/common/ConditionalScriptLoader';
import MetaTags from './components/common/MetaTags';
import { initializeFirebaseAnalytics, cleanupFirebaseAnalytics } from './firebase';
import AnalyticsManager from './components/analytics/AnalyticsManager';
import './styles/global.css';
import './styles/Auth.css';

// Import legal pages
import Impressum from './components/pages/Impressum';
import Datenschutz from './components/pages/Datenschutz';
import Terms from './components/pages/Terms';
// import { useComprehensiveAnalytics } from './hooks/useAnalytics';
// import { startPerformanceMonitoring } from './services/performanceService';

/**
 * Main application component
 * Configures routing and global providers
 * 
 * Note: We need to wrap AppProvider inside AuthProvider because
 * AuthProvider needs to set the admin status in the AppContext
 * based on Firebase custom claims
 */
const App: React.FC = () => {
  const { maintenance, loading } = useMaintenanceMode();
  if (loading) return null;
  return (
    <AuthProvider>
      <CookieConsentProvider>
        <AppProvider>
          {/* + Add ConditionalScriptLoader for analytics */}
          <ConditionalScriptLoader 
            category="analytics"
            serviceName="Firebase Analytics"
            initializeService={initializeFirebaseAnalytics}
            cleanupService={cleanupFirebaseAnalytics} />
          {/* Add MetaTags for social media sharing */}
          <MetaTags />
          <AdminRoleManager />
          {maintenance ? (
          <Maintenance />
        ) : (
          <Router>
            {/* Analytics and Performance Monitoring - Must be inside Router */}
            <AnalyticsManager />
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/consent" element={<Consent />} />
                <Route path="/experience" element={<Experience />} />
                <Route path="/feedback" element={<Feedback />} />
                <Route path="/apply" element={<Apply />} />                
                <Route path="/eventapplication/:id" element={<EventApplication />} />
                
                {/* Events Pages */}
                <Route path="/events" element={<EventsPage />} />
                <Route path="/events/:id" element={<EventDetailPage />} />
                
                {/* Legal Pages */}
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/terms" element={<Terms />} />
                
                {/* Admin Routes - Protected by ProtectedRoute with requireAdmin */}
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute requireAdmin>
                      <Admin />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/scanner" 
                  element={
                    <ProtectedRoute requireAdmin>
                      <QrScanner />
                    </ProtectedRoute>
                  } 
                />
                {/* Authentication Routes */}
                <Route 
                  path="/sign-in" 
                  element={
                    <PublicRoute>
                      <SignIn />
                    </PublicRoute>
                  } 
                />
                <Route 
                  path="/sign-up" 
                  element={
                    <PublicRoute>
                      <SignUp />
                    </PublicRoute>
                  } 
                />
                <Route 
                  path="/reset-password" 
                  element={
                    <PublicRoute>
                      <PasswordReset />
                    </PublicRoute>
                  } 
                />
                <Route 
                  path="/choose-username" 
                  element={
                    <ProtectedRoute>
                      <ChooseUsername />
                    </ProtectedRoute>
                  } 
                />                <Route 
                  path="/my-profile" 
                  element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/verify-email/callback"
                  element={<EmailVerificationRedirect />}
                />
                {/* 404 Not Found Route (must be last) */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
          </Router>
        )}
        </AppProvider>
      </CookieConsentProvider>
    </AuthProvider>
  );
};

export default App;
